/*
  Node Actions Context

  Provides node action handlers (like openDeleteModal, openOutcomeOptionsModal, openSwapVersionModal)
  to all node components without prop drilling through React Flow.
*/

import React, { createContext, useContext } from 'react';
import type { Node, Edge } from '../../types';

interface OutcomeOptionsParams {
  node: Node;
  nodeEdges: Edge[];
  journeyType?: string;
}

interface NodeActionModalParams {
  node: Node;
}

interface NodeActionsContextValue {
  openDeleteModal: (nodes: Node[]) => void;
  openOutcomeOptionsModal: (params: OutcomeOptionsParams) => void;
  openChangeWorkflowModal: (params: NodeActionModalParams) => void;
  openRetryModal: (params: NodeActionModalParams) => void;
  openSwapVersionModal: (params: NodeActionModalParams) => void;
  openStepUpExpirationModal: (params: NodeActionModalParams) => void;
}

const NodeActionsContext = createContext<NodeActionsContextValue | null>(null);

export const NodeActionsProvider: React.FC<{
  children: React.ReactNode;
  value: NodeActionsContextValue;
}> = ({ children, value }) => (
  <NodeActionsContext.Provider value={value}>
    {children}
  </NodeActionsContext.Provider>
);

export const useNodeActions = (): NodeActionsContextValue => {
  const context = useContext(NodeActionsContext);
  if (!context) {
    throw new Error('useNodeActions must be used within NodeActionsProvider');
  }
  return context;
};

